import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Save } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import { Input } from './NewsManager';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DEFAULT_COLORS = ['#d946ef', '#ec4899', '#a855f7', '#8b5cf6', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444'];

const emptyForm = { type: 'loisir', day: 'Lundi', timeStart: '17:00', timeEnd: '18:30', groupName: '', level: '', location: '', color: '#d946ef', order: 0 };

const ScheduleManager = () => {
  const [slots, setSlots] = useState([]);
  const [editing, setEditing] = useState(null); // 'new' or id
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState('loisir');

  const load = () => api.get('/schedule').then((r) => setSlots(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.groupName || !form.groupName.trim()) return toast.error('Le nom du groupe est obligatoire');
    if (!form.timeStart || !form.timeEnd) return toast.error('Merci de renseigner les horaires');
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === 'new') await api.post('/schedule', payload);
      else await api.put(`/schedule/${editing}`, payload);
      toast.success('Enregistré');
      setEditing(null);
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce créneau ?')) return;
    await api.delete(`/schedule/${id}`);
    toast.success('Supprimé');
    load();
  };

  const filtered = slots.filter((s) => s.type === tab).sort((a, b) => {
    const dayDiff = DAYS.indexOf(a.day) - DAYS.indexOf(b.day);
    if (dayDiff !== 0) return dayDiff;
    return (a.timeStart || '').localeCompare(b.timeStart || '');
  });

  return (
    <div data-testid="schedule-manager">
      <h2 className="text-2xl font-bold text-white mb-6">Planning hebdomadaire</h2>

      <div className="flex gap-2 mb-6 border-b border-white/10">
        {['loisir', 'competition'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`schedule-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'text-white border-fuchsia-500' : 'text-white/50 border-transparent hover:text-white/80'}`}
          >
            {t === 'loisir' ? 'Loisir' : 'Compétitions'}
          </button>
        ))}
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={() => { setForm({ ...emptyForm, type: tab }); setEditing('new'); }}
          data-testid="add-slot-btn"
          className="flex items-center gap-2 px-3 py-2 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 text-sm"
        >
          <Plus size={16} /> Ajouter un créneau
        </button>
      </div>

      <div className="grid gap-3">
        {filtered.length === 0 && <p className="text-white/50 text-sm">Aucun créneau pour cette catégorie.</p>}
        {filtered.map((s) => (
          <div key={s.id} className="min-w-0 flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
            <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium truncate">{s.groupName} <span className="text-white/50 text-sm">• {s.level}</span></h4>
              <p className="text-white/50 text-sm truncate">{s.day} • {s.timeStart} - {s.timeEnd} • {s.location}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => { setForm({ ...s }); setEditing(s.id); }} className="p-2 text-white/60 hover:text-fuchsia-400" data-testid={`edit-slot-${s.id}`}><Edit2 size={16} /></button>
              <button onClick={() => remove(s.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-slot-${s.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Créneau</h3>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X size={24} /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white">
                    <option value="loisir" className="bg-neutral-900">Loisir</option>
                    <option value="competition" className="bg-neutral-900">Compétitions</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Jour</label>
                  <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white">
                    {DAYS.map((d) => <option key={d} value={d} className="bg-neutral-900">{d}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Début</label>
                  <input type="time" value={form.timeStart} onChange={(e) => setForm({ ...form, timeStart: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white" />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Fin</label>
                  <input type="time" value={form.timeEnd} onChange={(e) => setForm({ ...form, timeEnd: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white" />
                </div>
              </div>

              <Input label="Groupe" testId="slot-group-input" value={form.groupName} onChange={(v) => setForm({ ...form, groupName: v })} placeholder="Baby GR, Poussines..." />
              <Input label="Niveau (optionnel)" testId="slot-level-input" value={form.level || ''} onChange={(v) => setForm({ ...form, level: v })} placeholder="Ex: Débutant, Régional..." />
              <Input label="Lieu" testId="slot-location-input" value={form.location || ''} onChange={(v) => setForm({ ...form, location: v })} placeholder="Gymnase La Saline..." />

              <div>
                <label className="block text-white/70 text-sm mb-2">Couleur du groupe</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {DEFAULT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${form.color === c ? 'border-white scale-110' : 'border-white/20'}`}
                      style={{ backgroundColor: c }}
                      aria-label={c}
                    />
                  ))}
                  <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-8 h-8 rounded-full bg-transparent border-0 cursor-pointer" />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={save} data-testid="save-slot-btn" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Save size={18} /> Enregistrer</button>
              <button onClick={() => setEditing(null)} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;
